import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../utils/api';
import { applyBranding, clearBranding } from '../utils/branding';
import { setOrgManifest, clearOrgManifest } from '../utils/pwaManifest';
import WorkspaceReadyModal from '../components/WorkspaceReadyModal';
import { AGE_GROUP_OPTIONS } from '../constants/ageGroups';

/**
 * Three flows:
 *  - login: existing user logs in
 *  - join: new user joins an existing org (parent/staff role on the default demo org)
 *  - register_org: brand new company signs up (creates its own tenant)
 *
 * Supports per-organization branding via /login/:slug (Phase B).
 * When a slug is present we fetch the public branding and render the
 * organization's logo, welcome message, tagline, and apply their brand
 * colors globally via CSS variables.
 */
function Login({ onLogin }) {
  const { slug: slugParam } = useParams();
  const [mode, setMode] = useState('login'); // login | join | register_org
  const [showAdvancedOrg, setShowAdvancedOrg] = useState(false);
  // Set when a new org has just been registered. Holds {slug, orgName, token, userData}
  // so we can show the WorkspaceReadyModal before pushing the owner to their dashboard.
  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [branding, setBranding] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(Boolean(slugParam));
  const [formData, setFormData] = useState({
    // common
    email: '',
    password: '',
    full_name: '',
    role: 'parent',
    phone: '',
    // organization-specific (register_org) — basic
    organization_name: '',
    org_type: 'single_center',
    website: '',
    company_number: '',
    vat_number: '',
    // join — center selection (used when joining a multi-center org)
    join_center_id: '',
    // branding
    currency: 'GBP',
    timezone: 'Europe/London',
    primary_color: '#00BCD4',
    secondary_color: '#FFC107',
    // first center — basic
    center_name: '',
    center_address: '',
    center_city: '',
    center_postcode: '',
    center_email: '',
    center_capacity: 50,
    center_ofsted_number: '',
    center_age_groups: ['0-2', '2-3', '3-5'],
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ------------------------------------------------------------------
  // (B) Inline reset-password hint after repeated failed login attempts
  // ------------------------------------------------------------------
  // Tracks consecutive failures on the *current session*. Reset to 0
  // when the user successfully logs in, switches mode, or edits their
  // email (likely a typo they're fixing). After >=2 failures we
  // surface a much more visible "Trouble signing in?" banner that
  // points straight at the password-reset flow — matches the
  // observation that nobody is using the existing tiny "Forgot
  // password?" link.
  const [failedLoginAttempts, setFailedLoginAttempts] = useState(0);

  // ------------------------------------------------------------------
  // (C) Email-typo suggestions ("did you mean gmail.com?")
  // ------------------------------------------------------------------
  // Curated list of the most common typos we observed in production
  // (gmal/gmial → gmail, hotnail → hotmail, etc.). We compare the
  // typed domain to this set both client-side (real-time hint under
  // the input) and at submit-time (last-chance confirm).
  const COMMON_EMAIL_TYPOS = React.useMemo(() => ({
    // gmail
    'gmal.com': 'gmail.com',
    'gmail.co': 'gmail.com',
    'gmial.com': 'gmail.com',
    'gmail.con': 'gmail.com',
    'gmaill.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gmaul.com': 'gmail.com',
    'gnail.com': 'gmail.com',
    // hotmail
    'hotnail.com': 'hotmail.com',
    'hotmai.com': 'hotmail.com',
    'hotmail.co': 'hotmail.com',
    'hotmail.con': 'hotmail.com',
    'hotmial.com': 'hotmail.com',
    'hotmali.com': 'hotmail.com',
    // yahoo
    'yaho.com': 'yahoo.com',
    'yahooo.com': 'yahoo.com',
    'yhoo.com': 'yahoo.com',
    'yaho.co.uk': 'yahoo.co.uk',
    'yahooo.co.uk': 'yahoo.co.uk',
    // outlook
    'outlok.com': 'outlook.com',
    'oultook.com': 'outlook.com',
    'outloo.com': 'outlook.com',
    // icloud
    'icloud.con': 'icloud.com',
    'iclod.com': 'icloud.com',
    // live
    'live.co': 'live.co.uk',
    'live.con': 'live.com',
  }), []);

  // Returns either null (looks fine) or a suggested email string.
  const suggestEmailFix = React.useCallback((rawEmail) => {
    const email = (rawEmail || '').trim().toLowerCase();
    if (!email.includes('@')) return null;
    const [local, domain] = email.split('@');
    if (!domain) return null;
    const corrected = COMMON_EMAIL_TYPOS[domain];
    if (corrected && corrected !== domain) {
      return `${local}@${corrected}`;
    }
    return null;
  }, [COMMON_EMAIL_TYPOS]);
  const emailSuggestion = suggestEmailFix(formData.email);

  // Load per-organization branding when slug is provided in URL
  useEffect(() => {
    let cancelled = false;
    // If a user lands on a branded /login/{slug} page while their UI is in
    // register_org mode (e.g. via back/forward navigation), force them back
    // into the sign-in mode — registering a brand-new tenant from inside
    // an existing organisation's private area is never the intended action.
    if (slugParam && mode === 'register_org') {
      setMode('login');
    }
    if (slugParam) {
      setBrandingLoading(true);
      // Swap the active PWA manifest so "Add to Home Screen" from this
      // branded page installs an app whose icon, name and launch URL
      // are the *client's*, not the platform default. Backend endpoint
      // returns per-org name/colour/icon and `start_url=/login/{slug}`.
      setOrgManifest(slugParam);
      api.getOrgBrandingBySlug(slugParam)
        .then((b) => {
          if (cancelled) return;
          setBranding(b);
          applyBranding(b);
        })
        .catch(() => {
          // Fall back to default branding silently if slug not found
          if (cancelled) return;
          setBranding(null);
          clearBranding();
          // Also restore the platform manifest so a typo in the slug
          // doesn't leave a stale branded manifest on the document.
          clearOrgManifest();
        })
        .finally(() => { if (!cancelled) setBrandingLoading(false); });
    } else {
      // Generic /login — reset to default Kids WiseApp
      clearBranding();
      clearOrgManifest();
      setBranding(null);
    }
    return () => { cancelled = true; };
  }, [slugParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const slugify = (text) =>
    (text || '')
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const res = await api.login(formData.email, formData.password);
        localStorage.setItem('token', res.access_token);
        const userData = await api.getMe();
        // (B) Reset the failure counter on a successful login so the
        // hint doesn't persist into the dashboard mount.
        setFailedLoginAttempts(0);
        onLogin(userData, res.access_token);
        return;
      }

      if (mode === 'join') {
        // Phone is mandatory at registration so the nursery can reach a
        // parent/staff in an emergency. Validate before hitting the API.
        const trimmedPhone = (formData.phone || '').trim();
        if (!trimmedPhone || trimmedPhone.replace(/\D/g, '').length < 7) {
          setError('Please enter a valid contact phone number.');
          setLoading(false);
          return;
        }
        // Slug-aware join: when the user is on /login/{slug} pass that slug
        // through so the backend attaches them to the correct tenant.
        // Without a slug we fall back to the default demo org (legacy /login).
        // Parents joining a multi-center org must pick which centre they belong
        // to; for single-center orgs we leave it undefined and the backend
        // auto-assigns the first centre.
        const joinCenterId = (
          formData.role === 'parent'
          && branding
          && Array.isArray(branding.centers)
          && branding.centers.length > 1
        ) ? formData.join_center_id : '';
        if (
          formData.role === 'parent'
          && branding
          && Array.isArray(branding.centers)
          && branding.centers.length > 1
          && !joinCenterId
        ) {
          setError('Please select which centre you would like to join.');
          setLoading(false);
          return;
        }
        const res = await api.register({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          role: formData.role,
          phone: formData.phone,
          organization_slug: slugParam || undefined,
          center_id: joinCenterId || undefined,
        });
        localStorage.setItem('token', res.access_token);
        // Mark this session as "fresh sign-up" so the parent dashboard
        // can jump straight to the Enrollment Wizard instead of stranding
        // a first-time parent on an empty "My Children" page. Consumed
        // and cleared on ParentDashboard mount.
        try {
          if (formData.role === 'parent') {
            sessionStorage.setItem('kw_just_registered', '1');
          }
        } catch (_) { /* private mode / SSR safety */ }
        const userData = await api.getMe();
        onLogin(userData, res.access_token);
        return;
      }

      if (mode === 'register_org') {
        const trimmedPhone = (formData.phone || '').trim();
        if (!trimmedPhone || trimmedPhone.replace(/\D/g, '').length < 7) {
          setError('Please enter a valid contact phone number.');
          setLoading(false);
          return;
        }
        const slug = slugify(formData.organization_name) || slugify(formData.email.split('@')[0]);
        const res = await api.registerOrganization({
          organization_name: formData.organization_name,
          slug,
          owner_email: formData.email,
          owner_password: formData.password,
          owner_name: formData.full_name,
          type: formData.org_type || 'single_center',
          website: formData.website || undefined,
          company_number: formData.company_number || undefined,
          vat_number: formData.vat_number || undefined,
          primary_color: formData.primary_color || undefined,
          secondary_color: formData.secondary_color || undefined,
          currency: formData.currency || undefined,
          timezone: formData.timezone || undefined,
          first_center: {
            center_id: 'tmp',
            name: formData.center_name || 'Main Center',
            address: formData.center_address || '',
            city: formData.center_city || '',
            postcode: formData.center_postcode || '',
            phone: formData.phone || '',
            email: formData.center_email || formData.email,
            capacity: parseInt(formData.center_capacity || '50', 10),
            age_groups: formData.center_age_groups && formData.center_age_groups.length > 0
              ? formData.center_age_groups
              : ['0-2', '2-3', '3-5'],
            ofsted_number: formData.center_ofsted_number || undefined,
            is_active: true,
          },
          contact_phone: formData.phone || undefined,
        });
        localStorage.setItem('token', res.access_token);
        const userData = await api.getMe();
        // Resolve the slug actually persisted on the server (may have a -1 / -2
        // suffix if the original was already taken). Fall back to the local one.
        let actualSlug = slug;
        try {
          const myOrg = await api.getMyOrganization();
          if (myOrg && myOrg.slug) actualSlug = myOrg.slug;
        } catch { /* keep client-side slug */ }

        // Stash login details and show the success modal. onLogin is deferred
        // until the user clicks "Continue to dashboard".
        setRegistrationSuccess({
          slug: actualSlug,
          orgName: formData.organization_name,
          token: res.access_token,
          userData,
        });
        return;
      }
    } catch (err) {
      const rawMsg = err.message || 'Something went wrong';
      const lowMsg = rawMsg.toLowerCase();

      // (B) Track failed login attempts so we can surface a more
      // prominent reset-password CTA after >=2 consecutive failures.
      // Only count actual auth failures here — validation errors (e.g.
      // bad phone format) are handled inline above and never reach
      // this catch.
      if (mode === 'login') {
        setFailedLoginAttempts(prev => prev + 1);
      }

      // (D) Friendlier message for "email already exists" during
      // registration. The backend currently returns a generic 400 with
      // text like "Email already registered" — we translate that into
      // an actionable hint that nudges the user to Sign In or reset
      // their password instead of creating yet another duplicate
      // account (the elicia30gilbert problem in production).
      if (mode !== 'login' && (
        lowMsg.includes('already registered')
        || lowMsg.includes('already exists')
        || lowMsg.includes('email is taken')
        || lowMsg.includes('duplicate')
      )) {
        setError(
          'An account with this email already exists. '
          + 'Click "Sign In" above to log in, or use "Forgot password?" '
          + 'if you can\'t remember your password.'
        );
      } else {
        setError(rawMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const titleByMode = {
    login: 'Welcome back',
    join: 'Create your account',
    register_org: 'Register your childcare service',
  };

  const subtitleByMode = {
    login: 'Sign in to continue',
    join: 'Join an existing Kids WiseApp workspace',
    register_org: 'Set up a new workspace for your organization',
  };

  return (
    <div className="login-container">
      {registrationSuccess && (
        <WorkspaceReadyModal
          slug={registrationSuccess.slug}
          orgName={registrationSuccess.orgName}
          onContinue={() => {
            const { userData, token } = registrationSuccess;
            setRegistrationSuccess(null);
            onLogin(userData, token);
          }}
        />
      )}
      <div className="login-card" data-testid="login-card">
        <div className="login-header" data-testid="login-header">
          {branding ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {/*
                Always reserve space for the org logo so every branded page
                feels polished. If a logo is uploaded, show it. If not, fall
                back to a letter-avatar in the org's primary colour. The
                onError handler also flips to the avatar if the image fails
                to load (broken / expired presigned URL / 404).
              */}
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt={branding.name}
                  style={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain' }}
                  data-testid="org-logo-top"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const avatar = document.getElementById('org-letter-avatar-fallback');
                    if (avatar) avatar.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                id="org-letter-avatar-fallback"
                aria-hidden={branding.logo_url ? 'true' : 'false'}
                data-testid="org-letter-avatar"
                style={{
                  display: branding.logo_url ? 'none' : 'flex',
                  width: 72, height: 72, borderRadius: '50%',
                  background: branding.primary_color || 'hsl(var(--primary))',
                  color: '#fff',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: '2rem', fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  fontFamily: 'inherit',
                }}
              >
                {(branding.name || '?').trim().charAt(0).toUpperCase()}
              </div>
              <h1 style={{ margin: 0, fontSize: '1.35rem' }} data-testid="org-welcome-heading">
                {branding.welcome_message || `Welcome to ${branding.name}`}
              </h1>
              {branding.tagline && (
                <p style={{ margin: 0, fontStyle: 'italic', opacity: 0.85, fontSize: '0.9rem' }} data-testid="org-tagline">
                  {branding.tagline}
                </p>
              )}
            </div>
          ) : (
            <h1>🎓 Kids WiseApp</h1>
          )}
          <p style={{ marginTop: '0.25rem' }}>{subtitleByMode[mode]}</p>
          {brandingLoading && (
            <p style={{ fontSize: '0.75rem', opacity: 0.6 }}>Loading workspace branding...</p>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1.5rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            className={`btn ${mode === 'login' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setMode('login'); setError(''); setFailedLoginAttempts(0); }}
            data-testid="mode-login-btn"
            style={{ minWidth: 100 }}
          >
            Sign In
          </button>
          <button
            type="button"
            className={`btn ${mode === 'join' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setMode('join'); setError(''); setFailedLoginAttempts(0); }}
            data-testid="mode-join-btn"
            style={{ minWidth: 100 }}
          >
            Join
          </button>
          {/* "New Company" was a top-level toggle here that competed
              for attention with the parent/staff sign-in flow. It now
              lives on its own dedicated page (`/register-your-nursery`)
              which pairs plan choice with a 14-day free trial. On this
              /login screen we only show a discreet link at the very
              bottom (see the "Are you a nursery owner?" panel below)
              so parents no longer see it as a peer option. */}
        </div>

        <h2 style={{ textAlign: 'center', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
          {titleByMode[mode]}
        </h2>

        {error && <div className="error-message" data-testid="error-message">{error}</div>}

        <form className="login-form" onSubmit={handleSubmit}>
          {/* Full Name - required for both join and register_org */}
          {mode !== 'login' && (
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                data-testid="full-name-input"
              />
            </div>
          )}

          {/* Organization Name - only for register_org */}
          {mode === 'register_org' && (
            <>
              <div style={{ padding: '0.75rem', background: 'hsl(var(--muted))', borderRadius: 'var(--radius)', marginBottom: '1rem', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                <strong>📋 Organization Registration</strong>
                <div>Fill in your company + first center details below.</div>
              </div>

              <h3 style={{ margin: '0.5rem 0', fontSize: '0.95rem' }}>🏢 Company Details</h3>
              <div className="form-group">
                <label>Organization / Company Name *</label>
                <input
                  type="text"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  placeholder="e.g. Sunshine Nursery Ltd"
                  required
                  data-testid="organization-name-input"
                />
              </div>
              <div className="form-group">
                <label>Organization Type</label>
                <select
                  value={formData.org_type}
                  onChange={(e) => setFormData({ ...formData, org_type: e.target.value })}
                  data-testid="org-type-select"
                >
                  <option value="single_center">Single Center</option>
                  <option value="multi_center">Multi-Center</option>
                  <option value="franchise">Franchise</option>
                </select>
              </div>

              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowAdvancedOrg(!showAdvancedOrg)}
                style={{ marginBottom: '1rem', width: '100%' }}
                data-testid="toggle-advanced-org"
              >
                {showAdvancedOrg ? '▲ Hide' : '▼ Show'} Advanced Organization Settings (optional)
              </button>

              {showAdvancedOrg && (
                <div style={{ background: 'hsl(var(--muted))', padding: '0.75rem', borderRadius: 'var(--radius)', marginBottom: '1rem' }}>
                  <div className="form-group">
                    <label>Website</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://www.yoursite.com"
                      data-testid="org-website-input"
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div className="form-group">
                      <label>Company Number</label>
                      <input
                        type="text"
                        value={formData.company_number}
                        onChange={(e) => setFormData({ ...formData, company_number: e.target.value })}
                        placeholder="UK Companies House"
                        data-testid="org-company-number-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>VAT Number</label>
                      <input
                        type="text"
                        value={formData.vat_number}
                        onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                        placeholder="GB123456789"
                        data-testid="org-vat-number-input"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div className="form-group">
                      <label>Currency</label>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        data-testid="org-currency-select"
                      >
                        <option value="GBP">£ GBP</option>
                        <option value="USD">$ USD</option>
                        <option value="EUR">€ EUR</option>
                        <option value="AUD">A$ AUD</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Timezone</label>
                      <select
                        value={formData.timezone}
                        onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                        data-testid="org-timezone-select"
                      >
                        <option value="Europe/London">Europe/London</option>
                        <option value="Europe/Dublin">Europe/Dublin</option>
                        <option value="America/New_York">America/New_York</option>
                        <option value="America/Los_Angeles">America/Los_Angeles</option>
                        <option value="Australia/Sydney">Australia/Sydney</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div className="form-group">
                      <label>Primary Brand Color</label>
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        style={{ height: '38px', padding: '2px' }}
                        data-testid="org-primary-color-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Secondary Brand Color</label>
                      <input
                        type="color"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        style={{ height: '38px', padding: '2px' }}
                        data-testid="org-secondary-color-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              <h3 style={{ margin: '0.75rem 0 0.5rem 0', fontSize: '0.95rem' }}>🏬 First Center / Location</h3>
              <div className="form-group">
                <label>Center / Location Name</label>
                <input
                  type="text"
                  value={formData.center_name}
                  onChange={(e) => setFormData({ ...formData, center_name: e.target.value })}
                  placeholder="e.g. Main Campus"
                  data-testid="center-name-input"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.center_address}
                  onChange={(e) => setFormData({ ...formData, center_address: e.target.value })}
                  placeholder="123 High Street"
                  data-testid="center-address-input"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.center_city}
                    onChange={(e) => setFormData({ ...formData, center_city: e.target.value })}
                    placeholder="London"
                    data-testid="center-city-input"
                  />
                </div>
                <div className="form-group">
                  <label>Postcode</label>
                  <input
                    type="text"
                    value={formData.center_postcode}
                    onChange={(e) => setFormData({ ...formData, center_postcode: e.target.value })}
                    placeholder="SW1A 1AA"
                    data-testid="center-postcode-input"
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label>Center Email (optional)</label>
                  <input
                    type="email"
                    value={formData.center_email}
                    onChange={(e) => setFormData({ ...formData, center_email: e.target.value })}
                    placeholder="defaults to owner email"
                    data-testid="center-email-input"
                  />
                </div>
                <div className="form-group">
                  <label>Capacity</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.center_capacity}
                    onChange={(e) => setFormData({ ...formData, center_capacity: e.target.value })}
                    data-testid="center-capacity-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>OFSTED Number (optional)</label>
                <input
                  type="text"
                  value={formData.center_ofsted_number}
                  onChange={(e) => setFormData({ ...formData, center_ofsted_number: e.target.value })}
                  placeholder="e.g. EY123456"
                  data-testid="center-ofsted-input"
                />
              </div>
              <div className="form-group">
                <label>Age Groups Accepted</label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', padding: '0.5rem', background: 'hsl(var(--background))', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}>
                  {AGE_GROUP_OPTIONS.map((ag) => (
                    <label key={ag} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.center_age_groups.includes(ag)}
                        onChange={(e) => {
                          const set = new Set(formData.center_age_groups);
                          if (e.target.checked) set.add(ag); else set.delete(ag);
                          setFormData({ ...formData, center_age_groups: Array.from(set) });
                        }}
                        data-testid={`center-age-group-${ag}`}
                      />
                      {ag} yrs
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                // (B) Editing the email after a failed attempt almost
                // always means the user is fixing a typo — clear the
                // counter so we don't keep nagging them.
                if (failedLoginAttempts > 0) setFailedLoginAttempts(0);
              }}
              required
              data-testid="email-input"
            />
            {/* (C) Inline typo suggestion. Shown as soon as the user
                types a recognised-typo domain. One-click to accept. */}
            {emailSuggestion && (
              <div
                data-testid="email-typo-suggestion"
                style={{
                  marginTop: '0.4rem',
                  padding: '0.45rem 0.65rem',
                  background: 'hsl(38 95% 95%)',
                  border: '1px solid hsl(38 90% 70%)',
                  borderRadius: 'var(--radius)',
                  fontSize: '0.82rem',
                  color: 'hsl(28 70% 28%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  flexWrap: 'wrap',
                }}
              >
                <span>💡 Did you mean</span>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, email: emailSuggestion })}
                  data-testid="email-typo-accept"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    fontSize: '0.85rem',
                    color: 'hsl(var(--primary))',
                    fontWeight: 700,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {emailSuggestion}
                </button>
                <span>?</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={6}
              data-testid="password-input"
            />
            {mode === 'login' && (
              <div style={{ marginTop: '0.55rem', textAlign: 'right' }}>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  style={{
                    // (A) Made the forgot-password link substantially
                    // more visible: bigger, bolder, with a subtle pill
                    // background so it doesn't disappear into the
                    // password label on mobile. Production data shows
                    // 0 reset attempts in 7 days while parents complain
                    // they can't sign in — the link was too easy to
                    // miss.
                    display: 'inline-block',
                    fontSize: '0.92rem',
                    color: 'hsl(var(--primary))',
                    textDecoration: 'none',
                    fontWeight: 700,
                    padding: '0.35rem 0.7rem',
                    borderRadius: 999,
                    background: 'hsl(var(--primary) / 0.08)',
                    border: '1px solid hsl(var(--primary) / 0.20)',
                  }}
                >
                  🔑 Forgot your password?
                </Link>
              </div>
            )}
          </div>

          {/* (B) High-visibility reset-password banner after 2+ failed
              login attempts. Renders only on the login form and is
              dismissed automatically when the user successfully logs
              in, switches mode, or edits their email. */}
          {mode === 'login' && failedLoginAttempts >= 2 && (
            <div
              data-testid="login-trouble-banner"
              style={{
                margin: '0.6rem 0 0.4rem 0',
                padding: '0.8rem 1rem',
                background: 'hsl(var(--primary) / 0.10)',
                border: '1px solid hsl(var(--primary) / 0.35)',
                borderRadius: 'var(--radius)',
                fontSize: '0.88rem',
                lineHeight: 1.4,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>
                Trouble signing in?
              </div>
              <div style={{ marginBottom: '0.6rem', color: 'hsl(var(--muted-foreground))' }}>
                Make sure the email is spelt correctly. If you've forgotten your password, you can reset it now — we'll email you a secure link.
              </div>
              <Link
                to="/forgot-password"
                data-testid="login-trouble-reset-cta"
                style={{
                  display: 'inline-block',
                  padding: '0.5rem 0.9rem',
                  background: 'hsl(var(--primary))',
                  color: '#fff',
                  borderRadius: 'var(--radius)',
                  textDecoration: 'none',
                  fontWeight: 700,
                  fontSize: '0.88rem',
                }}
              >
                Reset my password →
              </Link>
            </div>
          )}

          {mode === 'join' && (
            <>
              {slugParam && branding && (
                <div
                  data-testid="joining-org-hint"
                  style={{
                    padding: '0.6rem 0.85rem',
                    background: 'hsl(var(--primary) / 0.08)',
                    border: '1px solid hsl(var(--primary) / 0.25)',
                    borderRadius: 'var(--radius)',
                    fontSize: '0.85rem',
                    marginBottom: '0.75rem',
                    color: 'hsl(var(--foreground))',
                  }}
                >
                  🏷️ You're joining <strong>{branding.name}</strong>.
                  {formData.role === 'parent' && branding.require_parent_approval ? (
                    <> Your account will be created in a <strong>pending review</strong> state until an administrator at {branding.name} approves it.</>
                  ) : (
                    <> After registering, your account will be added to this workspace pending admin approval.</>
                  )}
                </div>
              )}
              <div className="form-group">
                <label>Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  data-testid="role-select"
                >
                  <option value="parent">Parent</option>
                  <option value="staff">Staff</option>
                  <option value="driver">Driver</option>
                </select>
              </div>

              {/* Center picker — only shown for parents joining a multi-center
                  branded workspace. Single-center orgs auto-assign so we keep
                  the form simple and avoid a redundant pick-1 dropdown. */}
              {formData.role === 'parent'
                && branding
                && Array.isArray(branding.centers)
                && branding.centers.length > 1 && (
                <div className="form-group" data-testid="join-center-picker-group">
                  <label>Choose your centre *</label>
                  <select
                    value={formData.join_center_id}
                    onChange={(e) => setFormData({ ...formData, join_center_id: e.target.value })}
                    required
                    data-testid="join-center-select"
                  >
                    <option value="">— Select a centre —</option>
                    {branding.centers.map((c) => (
                      <option key={c.center_id} value={c.center_id}>
                        {c.name}{c.city ? ` — ${c.city}` : ''}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem' }}>
                    Your account and bookings will be associated with this centre.
                  </small>
                </div>
              )}
            </>
          )}

          {mode !== 'login' && (
            <div className="form-group">
              <label>Phone <span style={{ color: 'hsl(var(--destructive))' }}>*</span></label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                minLength={7}
                placeholder="e.g. +44 7700 900123"
                data-testid="phone-input"
              />
              <small style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem' }}>
                Required so your provider can reach you in an emergency.
              </small>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading
              ? 'Please wait...'
              : mode === 'login'
              ? 'Sign In'
              : mode === 'join'
              ? 'Create Account'
              : 'Register Organization'}
          </button>
        </form>

        {/* Discreet "nursery owner" link — replaces the old top-level
            "New Company" tab so parents/staff no longer see it as a
            peer option to sign-in. Only shown on the generic /login
            page (never on a branded /login/{slug} because that's a
            tenant's own private area). */}
        {!slugParam && (
          <div
            data-testid="nursery-owner-hint"
            style={{
              marginTop: '1.25rem',
              padding: '0.75rem 0.9rem',
              background: 'hsl(190 65% 97%)',
              border: '1px solid hsl(190 60% 82%)',
              borderRadius: 8,
              fontSize: '0.8rem',
              color: 'hsl(200 55% 32%)',
              textAlign: 'center',
            }}
          >
            Are you a childcare provider looking to start a workspace?{' '}
            <a
              href="/register-your-nursery"
              style={{ fontWeight: 700, color: 'hsl(200 65% 40%)', textDecoration: 'none' }}
              data-testid="nursery-owner-cta"
            >
              Start your free trial →
            </a>
          </div>
        )}

        {!branding && (
          <div
            style={{
              marginTop: '1.5rem',
              padding: '1.25rem',
              background: 'hsl(var(--muted))',
              borderRadius: 'var(--radius)',
              border: '1px solid hsl(var(--border))',
            }}
            data-testid="value-prop-card"
          >
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'hsl(var(--primary))',
                marginBottom: '0.5rem',
              }}
            >
              Kids WiseApp
            </div>
            <div
              style={{
                fontSize: '1.05rem',
                fontWeight: 600,
                lineHeight: 1.35,
                marginBottom: '1rem',
              }}
            >
              The all-in-one childcare ERP for nurseries, after-school &amp; breakfast clubs, holiday clubs and childminders — and the families they care for.
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.6rem 1rem',
                fontSize: '0.825rem',
                color: 'hsl(var(--foreground))',
              }}
              data-testid="value-prop-features"
            >
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start' }}>
                <span aria-hidden="true" style={{ color: 'hsl(var(--primary))', fontWeight: 700 }}>✓</span>
                <span><strong>Smart bookings</strong> — sessions, holidays &amp; waitlists in one calendar.</span>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start' }}>
                <span aria-hidden="true" style={{ color: 'hsl(var(--primary))', fontWeight: 700 }}>✓</span>
                <span><strong>Digital chain of custody</strong> — secure check-in &amp; pickup.</span>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start' }}>
                <span aria-hidden="true" style={{ color: 'hsl(var(--primary))', fontWeight: 700 }}>✓</span>
                <span><strong>Auto timesheets</strong> — staff hours from clock-in to payroll.</span>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start' }}>
                <span aria-hidden="true" style={{ color: 'hsl(var(--primary))', fontWeight: 700 }}>✓</span>
                <span><strong>Daily moments</strong> — photos &amp; updates straight to parents.</span>
              </div>
            </div>
            <div
              style={{
                marginTop: '1rem',
                paddingTop: '0.85rem',
                borderTop: '1px dashed hsl(var(--border))',
                fontStyle: 'italic',
                fontSize: '0.8rem',
                color: 'hsl(var(--muted-foreground))',
                textAlign: 'center',
              }}
              data-testid="value-prop-quote"
            >
              “Every child deserves a setting that runs as well as it cares.”
            </div>
          </div>
        )}

        {/* Branded workspace footer (Phase A) */}
        {branding && (
          <div
            style={{
              marginTop: '1.5rem',
              paddingTop: '1rem',
              borderTop: '1px solid hsl(var(--border))',
              textAlign: 'center',
            }}
            data-testid="branded-footer"
          >
            {branding.logo_url && (
              <img
                src={branding.logo_url}
                alt={`${branding.name} logo`}
                style={{ maxHeight: 40, maxWidth: 140, objectFit: 'contain', opacity: 0.85, marginBottom: '0.5rem' }}
                data-testid="org-logo-footer"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
              <strong>{branding.name}</strong>
              {branding.website && (
                <>
                  {' · '}
                  <a
                    href={branding.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit' }}
                    data-testid="org-website-link"
                  >
                    Visit website
                  </a>
                </>
              )}
              {branding.contact_email && (
                <>
                  {' · '}
                  <a
                    href={`mailto:${branding.contact_email}`}
                    style={{ color: 'inherit' }}
                    data-testid="org-email-link"
                  >
                    {branding.contact_email}
                  </a>
                </>
              )}
            </div>
            <div
              data-testid="kidswiseapp-footer-lockup"
              style={{
                marginTop: '0.85rem',
                paddingTop: '0.85rem',
                borderTop: '1px solid hsl(var(--border))',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.4rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', opacity: 0.85 }}>
                <span style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Powered by
                </span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    color: 'hsl(var(--foreground))',
                  }}
                  aria-label="Kids WiseApp"
                >
                  <span aria-hidden="true" style={{ fontSize: '1.1rem', lineHeight: 1 }}>🎓</span>
                  <span>Kids&nbsp;WiseApp</span>
                </span>
              </div>
              <a
                href="/login"
                style={{
                  fontSize: '0.7rem',
                  color: 'hsl(var(--muted-foreground))',
                  textDecoration: 'underline',
                  opacity: 0.75,
                }}
                data-testid="register-new-company-link"
              >
                Register a new company →
              </a>
              {/* Legal links — required for GDPR / consumer law
                  compliance and visible on every login screen so
                  parents who land here from a branded URL can still
                  reach them. */}
              <div
                data-testid="login-legal-links"
                style={{
                  display: 'flex',
                  gap: '0.55rem',
                  fontSize: '0.7rem',
                  opacity: 0.75,
                  marginTop: '0.3rem',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <Link to="/privacy" style={{ color: 'hsl(var(--muted-foreground))', textDecoration: 'underline' }} data-testid="login-privacy-link">Privacy</Link>
                <span>·</span>
                <Link to="/terms" style={{ color: 'hsl(var(--muted-foreground))', textDecoration: 'underline' }} data-testid="login-terms-link">Terms</Link>
                <span>·</span>
                <Link to="/cookies" style={{ color: 'hsl(var(--muted-foreground))', textDecoration: 'underline' }} data-testid="login-cookies-link">Cookies</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
